/**
 * Send what went wrong in the browser to the server's log.
 *
 * The API logs its own failures; this is for the ones only the browser saw - an
 * uncaught exception, a promise nobody awaited, and every error a `catch` hid
 * behind a friendly sentence (`userMessage` in `errors.ts`). Each becomes a
 * `client_error` line in the same log as the server's, so both can be read and
 * alarmed on together (`docs/deployment/logging.md`).
 *
 * Reporting is best-effort and quiet: bounded per page load, deduplicated, never
 * shown to the person, and a report that fails is dropped rather than reported.
 */

import { apiResponse } from './client.ts';
import { setErrorReporter } from './errors.ts';

export interface ClientErrorReport {
  kind: 'error' | 'unhandledrejection' | 'api' | 'ui';
  message: string;
  stack?: string | null;
  endpoint?: string | null;
  status?: number | null;
  ref?: string | null;
  context?: string | null;
}

/** At most this many reports per page load - a render loop must not become a flood. */
const MAX_PER_PAGE = 20;
/** The same message again within this long is the same problem. */
const REPEAT_MS = 60_000;

let sent = 0;
const lastSeen = new Map<string, number>();

// Browser noise that is not a fault in the app.
const IGNORED: RegExp[] = [
  /ResizeObserver loop/, // benign, and fires in bursts
  /^Script error\.?$/, // a cross-origin script, with nothing to go on
];

function build(): string {
  return typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '';
}

/** Post one report. Never throws, never recurses. */
export function reportClientError(report: ClientErrorReport): void {
  const message = (report.message || '').slice(0, 1000);
  if (!message || IGNORED.some((p) => p.test(message))) return;
  const key = `${report.kind}|${message}`;
  const now = Date.now();
  if ((lastSeen.get(key) ?? 0) > now - REPEAT_MS) return;
  if (sent >= MAX_PER_PAGE) return;
  lastSeen.set(key, now);
  sent += 1;

  const body = {
    ...report,
    message,
    stack: report.stack?.slice(0, 4000) ?? null,
    // Path and query only: the host is known, and the fragment can hold state.
    page: `${location.pathname}${location.search}`.slice(0, 500),
    build: build(),
  };
  void apiResponse('/client-errors', { method: 'POST', body: JSON.stringify(body), timeoutMs: 10_000 }).catch(
    () => undefined,
  );
}

/** Report whatever was thrown, with what the person was doing. */
export function reportError(error: unknown, context?: string): void {
  if (error instanceof DOMException && error.name === 'AbortError') return;
  const api = error as { name?: string; status?: number; ref?: string | null; serverDetail?: string | null };
  if (api?.name === 'ApiError') {
    // The server logged its own 5xx under `ref`; signing in again is not a fault.
    if (api.status === 401 || api.status === 403) return;
    reportClientError({
      kind: 'api',
      message: api.serverDetail || (error as Error).message,
      status: api.status ?? null,
      ref: api.ref ?? null,
      context: context ?? null,
    });
    return;
  }
  if (error instanceof Error) {
    reportClientError({
      kind: 'ui',
      message: `${error.name}: ${error.message}`,
      stack: error.stack ?? null,
      context: context ?? null,
    });
    return;
  }
  reportClientError({ kind: 'ui', message: String(error), context: context ?? null });
}

/**
 * Listen for errors nothing caught, and let `userMessage` report the ones it
 * hides. Called once, at boot.
 */
export function installErrorReporting(): void {
  setErrorReporter(reportError);

  window.addEventListener('error', (event: ErrorEvent) => {
    const error = event.error as unknown;
    reportClientError({
      kind: 'error',
      message: error instanceof Error ? `${error.name}: ${error.message}` : event.message,
      stack: error instanceof Error ? (error.stack ?? null) : `${event.filename}:${event.lineno}:${event.colno}`,
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason as unknown;
    if (reason instanceof DOMException && reason.name === 'AbortError') return;
    const api = reason as { name?: string; status?: number };
    if (api?.name === 'ApiError') {
      if (api.status === 401 || api.status === 403) return;
      reportError(reason, 'unhandled');
      return;
    }
    reportClientError({
      kind: 'unhandledrejection',
      message: reason instanceof Error ? `${reason.name}: ${reason.message}` : String(reason),
      stack: reason instanceof Error ? (reason.stack ?? null) : null,
    });
  });
}

/** Test hook. */
export function resetErrorReporting(): void {
  sent = 0;
  lastSeen.clear();
}
